import React, { useState, useMemo } from 'react';
import { Calculator, Lock, Sparkles, TrendingDown, Building2, Briefcase, Cpu, Globe2 } from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { EMPTY_OBJECT } from '../../store/stableEmpties';
import { calculateIS } from '../../data/taxes';
import { projectAnnualProfit } from '../../utils/projectAnnualProfit';
import { INVESTMENTS } from '../../data/investments';
import { OFFICE_UPGRADES } from '../../data/officeUpgrades';
import { localize } from '../../data/i18nData';
import {
  INVESTMENT_CAPEX_CATEGORY,
  OFFICE_UPGRADE_CAPEX_CATEGORY,
  CAPEX_DURATIONS,
  CAPEX_CATEGORY_LABELS,
} from '../../utils/capexDepreciation';
import { formatMoney } from '../../utils/format';

/**
 * Simulateur prospectif d'investissement CAPEX — gated DAF (Tristan Midal).
 *
 * Mécanique :
 *   - L'apprenant choisit une immobilisation candidate (dépôt, bureau,
 *     logiciel, site internet) parmi les INVESTMENTS et OFFICE_UPGRADES
 *     éligibles (costBuy > 0 et catégorie connue) qu'il NE possède PAS déjà.
 *   - Il sélectionne un horizon (12, 24 ou 36 mois).
 *   - On calcule l'impact IS sur l'horizon choisi (somme des bénéfices
 *     annualisés taxés) avec et sans l'investissement, en respectant la
 *     durée comptable réelle (dépôts 20a, bureau 9a, logiciels 3a, site 2a).
 *
 * Différence avec `FiscalSimulator` : ce dernier ne couvre QUE l'année en
 * cours et QUE la flotte (5 ans). Ici on couvre plusieurs exercices et les
 * 4 catégories CAPEX non-flotte introduites en Phase B.
 *
 * Single source of truth : utilise les mêmes constantes (CAPEX_DURATIONS,
 * INVESTMENT_CAPEX_CATEGORY) que le moteur d'amortissement en jeu →
 * cohérence parfaite avec ce qui sera comptabilisé après l'achat.
 */

const CAT_ICON = {
  depot: Building2,
  office: Briefcase,
  software: Cpu,
  website: Globe2,
};

const CAT_DURATION_LABEL = {
  depot: '20 ans',
  office: '9 ans',
  software: '3 ans',
  website: '2 ans',
};

const HORIZON_OPTIONS = [12, 24, 36];

/**
 * Build the list of candidate CAPEX items the player could buy.
 * Filters out:
 *  - Items already owned (active in activeInvestments or officeUpgrades)
 *  - Items without a category in INVESTMENT_CAPEX_CATEGORY / OFFICE_UPGRADE_CAPEX_CATEGORY
 *  - Zero-cost items (location pure, contrat fournisseur — pas d'immobilisation)
 */
function buildCandidates(state) {
  const owned = state.activeInvestments || {};
  const officeOwned = state.officeUpgrades || {};
  const items = [];
  for (const inv of INVESTMENTS) {
    const cat = INVESTMENT_CAPEX_CATEGORY[inv.id];
    if (!cat) continue;
    if (!inv.costBuy || inv.costBuy <= 0) continue;
    if (owned[inv.id]) continue;
    items.push({ id: inv.id, label: localize('investment', inv).name, price: inv.costBuy, category: cat });
  }
  for (const upg of OFFICE_UPGRADES) {
    const cat = OFFICE_UPGRADE_CAPEX_CATEGORY[upg.id];
    if (!cat) continue;
    if (!upg.costBuy || upg.costBuy <= 0) continue;
    if (officeOwned[upg.id]) continue;
    items.push({ id: upg.id, label: upg.name, price: upg.costBuy, category: cat });
  }
  return items;
}

/**
 * Compute the cumulative IS over `horizonMonths` based on the player's
 * current annualized profit, with optional extra CAPEX depreciation.
 *
 * Returns total IS due over the horizon (sum of fractional yearly IS
 * for each in-window year).
 */
function computeCumulativeIs(monthlyProfit, monthlyExtraDot, horizonMonths) {
  if (horizonMonths <= 0) return 0;
  // We model years of 12 months for simplicity. The IS barème is applied
  // on annualized profit, prorated for partial years at the boundaries.
  let cumulative = 0;
  for (let m = 0; m < horizonMonths; m += 12) {
    const monthsInYear = Math.min(12, horizonMonths - m);
    const yearProfit = Math.max(0, (monthlyProfit - monthlyExtraDot) * monthsInYear);
    // For the IS barème to apply consistently, we extrapolate to an
    // annual basis then prorate back to the in-window fraction.
    const annualEquivalent = monthsInYear === 12
      ? yearProfit
      : (monthlyProfit - monthlyExtraDot) * 12;
    const fullYearIs = calculateIS(Math.max(0, annualEquivalent));
    cumulative += fullYearIs * (monthsInYear / 12);
  }
  return Math.round(cumulative);
}

export default function CapexSimulator() {
  const hasSupportUnlock = useGameStore(s => s.hasSupportUnlock);
  const annualProfit = useGameStore(s => s.annualProfit || 0);
  const month = useGameStore(s => s.month || 1);
  const activeInvestments = useGameStore(s => s.activeInvestments || EMPTY_OBJECT);
  const officeUpgrades = useGameStore(s => s.officeUpgrades || EMPTY_OBJECT);

  const isUnlocked = typeof hasSupportUnlock === 'function' && hasSupportUnlock('fiscal_simulator');

  const candidates = useMemo(
    () => buildCandidates({ activeInvestments, officeUpgrades }),
    [activeInvestments, officeUpgrades]
  );

  const [selectedId, setSelectedId] = useState(candidates[0]?.id || '');
  const [horizonMonths, setHorizonMonths] = useState(24);

  if (!isUnlocked) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-amber-700/40 p-4"
        data-testid="capex-simulator-locked"
      >
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-full bg-amber-500/15">
            <Lock className="w-4 h-4 text-amber-300" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-amber-200">Simulateur CAPEX — DAF requis</p>
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">
              Cet outil prospectif d&apos;arbitrage d&apos;investissement est verrouillé tant
              que vous n&apos;avez pas recruté un{' '}
              <strong className="text-slate-200">Directeur administratif et financier</strong>{' '}
              (Tristan Midal, Senior). Une fois le DAF en poste, vous pourrez simuler
              l&apos;impact IS d&apos;un achat de dépôt, bureau, logiciel ou site internet sur
              12 à 36 mois.
            </p>
            <p className="text-[10px] text-slate-500 mt-2 italic">
              Astuce — recrutez d&apos;abord Sophie Lambert (Comptable), prérequis pour
              débloquer le DAF.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (candidates.length === 0) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-cyan-700/30 p-4"
        data-testid="capex-simulator-empty"
      >
        <div className="flex items-center gap-2 mb-2">
          <Calculator className="w-4 h-4 text-cyan-300" />
          <p className="text-sm font-semibold text-slate-200">Simulateur CAPEX — DAF</p>
        </div>
        <p className="text-xs text-slate-400 italic leading-relaxed">
          Toutes les immobilisations éligibles (dépôts, bureau, logiciels, site internet)
          sont déjà à votre actif. Plus rien à simuler — bravo pour la couverture CAPEX !
        </p>
      </div>
    );
  }

  const selected = candidates.find(c => c.id === selectedId) || candidates[0];
  const Icon = CAT_ICON[selected.category] || Building2;

  // Build the IS projection :
  //   - Annualised current profit = projectAnnualProfit (lissage début d'année).
  //   - Monthly profit ≈ annualised / 12.
  //   - Monthly extra dotation = price / CAPEX_DURATIONS[category] (in months).
  const monthsElapsed = Math.max(month, 1);
  const annualisedProfit = projectAnnualProfit({ annualProfit, month }).annualProfit;
  const monthlyProfit = annualisedProfit / 12;
  const totalMonths = CAPEX_DURATIONS[selected.category];
  const monthlyExtraDot = selected.price / totalMonths;
  // Cap the extra dotation contribution by the horizon (if horizon < depreciation duration).
  const dotMonthsInHorizon = Math.min(horizonMonths, totalMonths);
  const totalExtraDot = Math.round(monthlyExtraDot * dotMonthsInHorizon);

  const isBaseline = computeCumulativeIs(monthlyProfit, 0, horizonMonths);
  const isWith = computeCumulativeIs(monthlyProfit, monthlyExtraDot, horizonMonths);
  const isSaved = Math.max(0, isBaseline - isWith);
  const netCost = selected.price - isSaved;

  return (
    <div
      className="bg-ink-800 rounded-lg border border-cyan-700/30 p-4"
      data-testid="capex-simulator"
    >
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Calculator className="w-4 h-4 text-cyan-300" />
        <p className="text-sm font-semibold text-slate-200">Simulateur CAPEX — DAF</p>
        <span
          className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 flex items-center gap-1"
          title="Outil exclusif débloqué par votre DAF"
        >
          <Sparkles className="w-3 h-3" /> DAF exclusif
        </span>
        <span className="text-[10px] text-slate-500 ml-auto">
          Compare l&apos;IS cumulé sur 12-36 mois avant l&apos;achat
        </span>
      </div>

      {/* Pickers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-3">
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          Immobilisation simulée
          <select
            value={selected.id}
            onChange={(e) => setSelectedId(e.target.value)}
            className="bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-slate-200 text-xs"
            data-testid="capex-sim-item"
          >
            {candidates.map(c => (
              <option key={c.id} value={c.id}>
                [{CAPEX_CATEGORY_LABELS[c.category]}] {c.label} — {formatMoney(c.price)}
              </option>
            ))}
          </select>
        </label>
        <label className="text-xs text-slate-400 flex flex-col gap-1">
          Horizon de simulation
          <select
            value={horizonMonths}
            onChange={(e) => setHorizonMonths(Number(e.target.value))}
            className="bg-ink-900 border border-ink-600 rounded px-2 py-1.5 text-slate-200 text-xs"
            data-testid="capex-sim-horizon"
          >
            {HORIZON_OPTIONS.map(n => (
              <option key={n} value={n}>{n} mois ({n / 12} an{n / 12 > 1 ? 's' : ''})</option>
            ))}
          </select>
        </label>
      </div>

      {/* Selected item summary */}
      <div className="mb-3 p-2.5 rounded bg-ink-700/40 border border-ink-600 flex items-center gap-2.5">
        <Icon className="w-4 h-4 text-cyan-300 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-slate-200 truncate">{selected.label}</p>
          <p className="text-[10px] text-slate-500">
            {CAPEX_CATEGORY_LABELS[selected.category]} · Amortissement {CAT_DURATION_LABEL[selected.category]} ({totalMonths} mois)
          </p>
        </div>
        <div className="text-right">
          <p className="text-[10px] text-slate-500">Capex</p>
          <p className="text-xs font-bold text-slate-200">{formatMoney(selected.price)}</p>
        </div>
      </div>

      {/* IS comparison */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="bg-ink-700/60 rounded p-2 text-center" data-testid="capex-sim-is-base">
          <p className="text-[10px] text-slate-500">IS cumulé (sans achat)</p>
          <p className="text-sm font-bold text-slate-200">{formatMoney(isBaseline)}</p>
        </div>
        <div className="bg-ink-700/60 rounded p-2 text-center" data-testid="capex-sim-is-with">
          <p className="text-[10px] text-slate-500">IS cumulé (avec achat)</p>
          <p className="text-sm font-bold text-cyan-300">{formatMoney(isWith)}</p>
        </div>
        <div
          className="bg-emerald-900/30 border border-emerald-700/40 rounded p-2 text-center"
          data-testid="capex-sim-saved"
        >
          <p className="text-[10px] text-slate-500 flex items-center justify-center gap-0.5">
            <TrendingDown className="w-3 h-3" /> Économie IS
          </p>
          <p className="text-sm font-bold text-emerald-300">{formatMoney(isSaved)}</p>
        </div>
        <div
          className={`rounded p-2 text-center border ${
            netCost > 0
              ? 'bg-ink-700/60 border-ink-600'
              : 'bg-emerald-900/30 border-emerald-700/40'
          }`}
          data-testid="capex-sim-net-cost"
        >
          <p className="text-[10px] text-slate-500">Coût net après IS</p>
          <p className={`text-sm font-bold ${netCost > 0 ? 'text-amber-300' : 'text-emerald-300'}`}>
            {formatMoney(Math.max(0, netCost))}
          </p>
        </div>
      </div>

      {/* DAF interpretation */}
      <div className="text-[11px] text-slate-400 leading-relaxed">
        <strong className="text-cyan-300">Décryptage DAF&nbsp;:</strong>{' '}
        achat de <span className="text-slate-200 font-bold">{selected.label}</span> à{' '}
        <span className="text-slate-200 font-bold">{formatMoney(selected.price)}</span> →{' '}
        dotation supplémentaire cumulée sur l&apos;horizon{' '}
        <span className="text-amber-300 font-bold">{formatMoney(totalExtraDot)}</span>{' '}
        (sur <span className="text-slate-200">{dotMonthsInHorizon}</span>/{totalMonths} mois d&apos;amortissement).
        {' '}Bénéfice annualisé actuel : <span className="text-slate-200">{formatMoney(Math.round(annualisedProfit))}</span>/an.
        {isSaved >= 1000 && (
          <span className="block mt-1 text-emerald-300 italic">
            💡 Recommandation : économie d&apos;IS {'>'} {formatMoney(1000)} sur l&apos;horizon — l&apos;investissement allège significativement la fiscalité.
          </span>
        )}
        {isSaved < 500 && annualisedProfit < 10000 && (
          <span className="block mt-1 text-amber-300 italic">
            ⚠️ Attention : votre bénéfice annualisé est faible. L&apos;impact IS sera limité — pesez l&apos;achat en fonction de la rentabilité opérationnelle, pas seulement de l&apos;avantage fiscal.
          </span>
        )}
      </div>
    </div>
  );
}
